import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import AdminNavbar from '../components/AdminNavbar';
import AdminTutors from '../components/AdminTutors';
import AdminTutees from '../components/AdminTutees';
import TuteeProfile from '../components/TuteeProfile';
import TutorDetail from '../components/TutorDetail';
import MonthlyReport from '../components/MonthlyReport';
import FamilyDetail from '../components/FamilyDetail';
import ActivityLog from '../components/ActivityLog';
import NotificationList from '../components/NotificationList';
import NotificationPreferences from '../components/NotificationPreferences';
import ClaimsManagement from '../components/ClaimsManagement';
import ProfitSharerAdjustmentAlert from '../components/ProfitSharerAdjustmentAlert';

import InvoiceManagement from '../components/InvoiceManagement';
import InvoiceTemplateSettings from '../components/InvoiceTemplateSettings';
import TuteeInvoiceManagement from '../components/TuteeInvoiceManagement';
import FinanceICDashboard from '../components/FinanceICDashboard';
import { setAuthToken } from '../services/api';
import '../assets/AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('tutees'); // Default to tutees (accessible by both)
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    // Set the auth token in the API service
    setAuthToken(token);
    
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      
      // Set default tab based on role
      if (userData.role === 'admin') {
        setActiveTab('tutors');
      } else if (userData.role === 'finance') {
        setActiveTab('tutees'); // Finance starts with tutees
      }
    }
  }, [navigate]);

  // Sync activeTab with current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/tutors')) {
      setActiveTab('tutors');
    } else if (path.includes('/tutees') || path.includes('/families')) {
      setActiveTab('tutees');
    } else if (path.includes('/lessons')) {
      setActiveTab('lessons');
    } else if (path.includes('/activity')) {
      setActiveTab('activity');
    } else if (path.includes('/invoices') || path.includes('/invoice-settings')) {
      setActiveTab('invoices');
    } else if (path.includes('/tutee-invoices')) {
      setActiveTab('tutee-invoices');
    } else if (path.includes('/claims')) {
      setActiveTab('claims');
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    // Remove the auth token from the API service
    setAuthToken(null);
    navigate('/');
  };

  return (
    <div className="admin-dashboard" style={{ width: '100%', minHeight: '100vh' }}>
      <AdminNavbar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        user={user}
        onLogout={handleLogout}
      />
      
      {/* Persistent alert for profit sharer adjustments */}
      {user && user.role === 'admin' && <ProfitSharerAdjustmentAlert />}
      
      <div className="dashboard-content" style={{ width: '100%', padding: '0' }}>
        <Routes>
          <Route path="/" element={
            <>
              {activeTab === 'tutors' && <AdminTutors />}
              {activeTab === 'tutees' && <AdminTutees />}
              {activeTab === 'lessons' && <MonthlyReport />}
              {activeTab === 'activity' && <ActivityLog />}
              {activeTab === 'invoices' && <InvoiceManagement />}
              {activeTab === 'tutee-invoices' && <TuteeInvoiceManagement />}
              {activeTab === 'claims' && <ClaimsManagement />}
            </>
          } />
          <Route path="tutees" element={<AdminTutees />} />
          <Route path="tutees/:id" element={<TuteeProfile />} />
          <Route path="tutors" element={<AdminTutors />} />
          <Route path="tutors/:id" element={<TutorDetail />} />
          <Route path="lessons" element={<MonthlyReport />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="families/:familyNumber" element={<FamilyDetail />} />
          <Route path="notifications" element={<NotificationList />} />
          <Route path="preferences" element={<NotificationPreferences />} />

          <Route path="invoices" element={<InvoiceManagement />} />
          <Route path="tutee-invoices" element={<TuteeInvoiceManagement />} />
          <Route path="finance-ic-dashboard" element={<FinanceICDashboard />} />
          <Route path="claims" element={<ClaimsManagement />} />
          <Route path="invoice-settings" element={<InvoiceTemplateSettings />} />
          {/* Catch-all: any unmatched admin path falls back to the dashboard home
              instead of rendering a blank content area */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;